import { motion } from "framer-motion";
import { ArrowRight, Check, Sunrise, Wrench } from "lucide-react";
import { scrollToSection } from "@/constants";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const RESULTS = [
  "Employees who better understand AI",
  "Practical AI use cases",
  "Repeatable prompts and workflows",
  "Ideas for automation",
  "Areas for further exploration",
  "A clearer roadmap toward AI adoption",
];

export default function SignatureDay() {
  return (
    <section id="signature" data-testid="signature-section" className="py-32 lg:py-40 bg-[#2C2A29] text-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.p {...fade} transition={{ duration: 0.6, ease: "easeOut" }} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-[#D9734E]">
          Signature Experience
        </motion.p>
        <motion.h2 {...fade} transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-6 max-w-4xl">
          The AI-Ready <span className="italic text-[#D9734E]">Organization Day</span>
        </motion.h2>
        <motion.p {...fade} transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }} className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed font-light text-[#FAF9F6]/75">
          One high-impact day designed to move your organization from AI curiosity
          to practical adoption.
        </motion.p>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <motion.div {...fade} transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }} data-testid="signature-morning" className="rounded-[2rem] border border-[#FAF9F6]/15 p-10 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <Sunrise className="w-6 h-6 text-[#D9734E]" />
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-[#FAF9F6]/60">Morning</span>
            </div>
            <h3 className="font-display text-3xl tracking-tight">Understand the Shift</h3>
            <ul className="mt-8 space-y-4 text-base font-light text-[#FAF9F6]/75">
              <li>What AI is — and what it isn't.</li>
              <li>How organizations are using AI today.</li>
              <li>Where opportunities and risks exist.</li>
              <li>Responsible and secure AI use.</li>
              <li>Practical prompting strategies.</li>
            </ul>
          </motion.div>

          <motion.div {...fade} transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }} data-testid="signature-afternoon" className="rounded-[2rem] border border-[#FAF9F6]/15 p-10 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <Wrench className="w-6 h-6 text-[#D9734E]" />
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-[#FAF9F6]/60">Afternoon</span>
            </div>
            <h3 className="font-display text-3xl tracking-tight">Build With It</h3>
            <ul className="mt-8 space-y-4 text-base font-light text-[#FAF9F6]/75">
              <li>Participants identify real work they can improve using AI.</li>
              <li>They create prompts, workflows, and solutions based on their actual responsibilities.</li>
              <li>Teams leave with practical tools they can begin using immediately.</li>
            </ul>
          </motion.div>
        </div>

        <motion.div {...fade} transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }} data-testid="signature-result" className="mt-10 rounded-[2rem] bg-[#D9734E] p-10 md:p-12">
          <h3 className="font-display text-3xl tracking-tight">The Result</h3>
          <p className="mt-4 text-base font-light text-white/85">By the end of the experience, your organization will have:</p>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {RESULTS.map((r) => (
              <li key={r} className="flex items-start gap-3 text-base text-white">
                <Check className="w-4 h-4 mt-1 shrink-0" />
                {r}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.button
          {...fade}
          transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
          onClick={() => scrollToSection("#contact")}
          data-testid="signature-cta-button"
          className="mt-14 inline-flex items-center gap-3 rounded-full bg-[#FAF9F6] text-[#2C2A29] px-8 py-4 font-semibold hover:-translate-y-1 hover:shadow-lg hover:shadow-black/30 transition-transform duration-300"
        >
          Talk About Bringing This Program to Your Organization
          <ArrowRight className="w-5 h-5" />
        </motion.button>
      </div>
    </section>
  );
}
